/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { CostCenterFinderListItem } from '@gh/builder-shared';
import {
  ScheduleTaskChangeDateAfterCompleteType,
  ScheduleTaskType,
  ScheduleTemplateDetailsDto,
  ScheduleTemplateStatus,
  ScheduleTemplateTaskListItemDto,
  ScheduleType,
} from '@gh/builder-shared-data';
import { isDirtyTypeNew } from '@gh/common-shared';
import { DirtyType, EMPTY_ID, ID, WithVersion } from '@gh/common-shared-data';
import { addDays, isNotNil, setHours } from '@gh/core-util';
import {
  calculateHierarchicalRelations,
  GANTT_SCHEDULE_UNIQUE_ID,
  GANTT_WORKING_HOURS,
  GanttTask,
  ganttTaskRelationFromDto,
  ganttTaskRelationToDto,
  genGanttTaskUniqueId,
  getGanttTaskNumberFromId,
  HOURS_END_OF_DAY,
} from '@gh/builder-shared-gantt-chart';
import { find, get, map, omit } from 'lodash';

export type ScheduleTemplateGanttTask = GanttTask & {
  costCenterId?: ID;
  criticalPathId?: ID;
  changeDateAfterComplete?: ScheduleTaskChangeDateAfterCompleteType;
  isSlab: boolean;
  isInSupervisorSchedule: boolean;
};
export type ScheduleTemplateGanttDetails = WithVersion<{
  templateId?: ID;
  name: string;
  duration: number;
  active: ScheduleTemplateStatus;
  type: ScheduleType;
  tasks: ScheduleTemplateGanttTask[];
}>;

export const SCHEDULE_TEMPLATE_TASK_START_DATE = new Date(0);

export const scheduleTemplateGanttDetailsFromDto = (dto: ScheduleTemplateDetailsDto): ScheduleTemplateGanttDetails => ({
  ...<ScheduleTemplateGanttDetails>omit(dto, 'tasks'),
  tasks: calculateHierarchicalRelations(dto.tasks.map((task) => ({
    // @ts-ignore
    ...<ScheduleTemplateGanttTask>omit(task, 'templateTaskId', 'parentTaskNumber', 'duration'),
    duration: task.duration / GANTT_WORKING_HOURS,
    physicalTaskId: task.templateTaskId!, // tslint:disable-line:no-non-null-assertion
    scheduleId: GANTT_SCHEDULE_UNIQUE_ID,
    taskId: genGanttTaskUniqueId(task.taskNumber),
    parentTaskId: isNotNil(task.parentTaskNumber) ? genGanttTaskUniqueId(task.parentTaskNumber) : void 0,
    opened: false,
    noteMemo: task.recentNote ? task.recentNote.memo : '',
    resource: {
      name: task.resource,
      id: task.supplierContactId || task.employeeContactId,
    },
    predecessors: map(
      task.predecessors,
      (predecessor) => ganttTaskRelationFromDto(GANTT_SCHEDULE_UNIQUE_ID, predecessor)),
  }))),
});

export const scheduleTemplateGanttDetailsToDto = (model: ScheduleTemplateGanttDetails): ScheduleTemplateDetailsDto => ({
  ...<ScheduleTemplateDetailsDto>omit(model, 'tasks'),
  tasks: model.tasks.map((task) => ({
    ...<ScheduleTemplateTaskListItemDto>omit(
      task,
      [
        'scheduleId', 'physicalTaskId', 'taskId', 'parentTaskId', 'completionPct', 'completionDate',
        'opened', 'hasChildren', 'canBeDeleted', 'dirtyType', 'entityState', 'hasChildrenExcludedFromGanttChart',
        'duration', 'selected', 'notes', 'noteMemo', 'mayHaveResource', 'isResourceInUse', 'markedAsDeleted', 'canBeMarkedAsDeleted',
      ]),
    duration: task.duration * GANTT_WORKING_HOURS,
    type: task.hasChildrenExcludedFromGanttChart && task.type === ScheduleTaskType.Empty
      ? ScheduleTaskType.Summary
      : task.type,
    notes: [],
    resource: task.resource && task.resource.name ? task.resource.name : undefined,
    supplierContactId: task.resource && task.resource.id && !task.resource.isEmployee ? task.resource.id : undefined,
    employeeContactId: task.resource && task.resource.id && task.resource.isEmployee ? task.resource.id : undefined,
    templateTaskId: isDirtyTypeNew(task.dirtyType) ? void 0 : task.physicalTaskId,
    parentTaskNumber: task.parentTaskId ? getGanttTaskNumberFromId(task.parentTaskId, model.tasks) : void 0,
    predecessors: map(task.predecessors, ganttTaskRelationToDto),
  })),
});

export const newScheduleTemplateCostCenterTask = (taskNumber: number,
                                                  cc: CostCenterFinderListItem,
                                                  startDate: Date): ScheduleTemplateGanttTask => {
  const supplier = ({ suppliers }: CostCenterFinderListItem) =>
    find(suppliers, ({ isPreferred }) => isPreferred);

  return {
    physicalTaskId: EMPTY_ID,
    scheduleId: GANTT_SCHEDULE_UNIQUE_ID,
    taskId: genGanttTaskUniqueId(taskNumber),
    type: ScheduleTaskType.CostCenter,
    hasChildrenExcludedFromGanttChart: false,
    supplierContactId: get(supplier(cc), 'id', void 0),
    taskNumber,
    name: cc.name,
    resource: {
      name: get(supplier(cc), 'name', void 0),
      id: get(supplier(cc), 'id', void 0),
    },
    costCenterId: cc.id,
    isSlab: false,
    isInSupervisorSchedule: false,
    isExcludeFromGanttChart: false,
    isFromCriticalPath: false,
    isResourceInUse: false,
    startDate,
    // tslint:disable-next-line:no-non-null-assertion
    endDate: addDays(setHours(startDate, HOURS_END_OF_DAY), cc.scheduledDays! - 1),
    duration: cc.scheduledDays!, // tslint:disable-line:no-non-null-assertion
    canBeDeleted: true,
    opened: false,
    hasChildren: false,
    dirtyType: DirtyType.New,
    predecessors: [],
    todo: [],
    notes: [],
    mayHaveResource: true,
    markedAsDeleted: false,
    canBeMarkedAsDeleted: false,
  };
};

export const newScheduleTemplateEmptyTask = (taskNumber: number,
                                             name: string,
                                             startDate: Date,
                                             parentTaskId?: ID): ScheduleTemplateGanttTask =>
  ({
    physicalTaskId: EMPTY_ID,
    scheduleId: GANTT_SCHEDULE_UNIQUE_ID,
    taskId: genGanttTaskUniqueId(taskNumber),
    type: ScheduleTaskType.Empty,
    hasChildrenExcludedFromGanttChart: false,
    taskNumber,
    parentTaskId,
    name,
    isSlab: false,
    isInSupervisorSchedule: false,
    isExcludeFromGanttChart: false,
    isFromCriticalPath: false,
    startDate,
    // tslint:disable-next-line:no-non-null-assertion
    endDate: setHours(startDate, HOURS_END_OF_DAY),
    duration: 1, // tslint:disable-line:no-non-null-assertion
    canBeDeleted: true,
    opened: false,
    isResourceInUse: false,
    hasChildren: false,
    dirtyType: DirtyType.New,
    predecessors: [],
    todo: [],
    notes: [],
    mayHaveResource: true,
    markedAsDeleted: false,
    canBeMarkedAsDeleted: false,
  });
